import { useEffect } from "react";

/**
 * Loads a HubSpot form into the given container id and injects custom styles
 * into the same-origin iframe so the form matches the site design.
 */
export const useHubSpotForm = ({ containerId, portalId, formId, region = "na1" }) => {
  useEffect(() => {
    const loadForm = () => {
      const container = document.getElementById(containerId);
      if (!window.hbspt || !window.hbspt.forms || !container) return;
      // Safer DOM clear than innerHTML="" (no parser involved)
      while (container.firstChild) container.removeChild(container.firstChild);
      window.hbspt.forms.create({
        portalId,
        formId,
        region,
        target: `#${containerId}`,
        onFormReady: () => {
          const tryInject = (attempts = 0) => {
            const iframe = container.querySelector("iframe");
            if (!iframe || !iframe.contentDocument) {
              if (attempts < 30) setTimeout(() => tryInject(attempts + 1), 200);
              return;
            }
            const doc = iframe.contentDocument;
            if (doc.getElementById("valubi-hs-overrides")) return;
            const style = doc.createElement("style");
            style.id = "valubi-hs-overrides";
            style.textContent = `
              body, html, form.hs-form { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; }
              .hs-form-field { margin-bottom: 1.25rem !important; }
              .hs-form-field > label {
                font-family: 'Inter', sans-serif !important;
                font-size: 0.875rem !important;
                font-weight: 500 !important;
                color: #334155 !important;
                margin-bottom: 0.5rem !important;
                display: block !important;
              }
              .hs-form-required { color: #0D9488 !important; margin-left: 4px; }
              .hs-input, input[type="text"], input[type="email"], input[type="tel"], select.hs-input {
                width: 100% !important;
                padding: 0.75rem 1rem !important;
                border: 1px solid #E2E8F0 !important;
                border-radius: 0.5rem !important;
                font-family: 'Inter', sans-serif !important;
                font-size: 1rem !important;
                color: #0F172A !important;
                background-color: #FFFFFF !important;
                box-sizing: border-box !important;
                transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
              }
              .hs-input:focus, input:focus, select:focus {
                outline: none !important;
                border-color: #0D9488 !important;
                box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15) !important;
              }
              .hs_submit, .hs_submit .actions, form .actions { width: 100% !important; padding: 0 !important; margin: 0 !important; }
              .hs-button, input[type="submit"].hs-button, .actions input.hs-button {
                width: 100% !important;
                display: block !important;
                padding: 1rem 1.5rem !important;
                background: #0D9488 !important;
                background-image: none !important;
                background-color: #0D9488 !important;
                color: #FFFFFF !important;
                font-family: 'Inter', sans-serif !important;
                font-weight: 600 !important;
                font-size: 1rem !important;
                line-height: 1.5 !important;
                border: none !important;
                border-radius: 0.75rem !important;
                cursor: pointer !important;
                margin-top: 0.5rem !important;
                box-shadow: none !important;
                text-shadow: none !important;
                transition: background-color 0.2s ease !important;
              }
              .hs-button:hover, input[type="submit"].hs-button:hover {
                background-color: #0F766E !important;
              }
              .hs-error-msgs { list-style: none !important; padding: 0 !important; margin: 0.5rem 0 0 0 !important; }
              .hs-error-msgs li, .hs-error-msg { color: #DC2626 !important; font-size: 0.875rem !important; }
            `;
            doc.head.appendChild(style);
          };
          tryInject();
        }
      });
    };

    const timer = setTimeout(() => {
      const existingScript = document.querySelector('script[src*="hsforms"]');
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "//js.hsforms.net/forms/embed/v2.js";
        script.charset = "utf-8";
        script.async = true;
        script.onload = () => setTimeout(loadForm, 500);
        document.body.appendChild(script);
      } else if (window.hbspt && window.hbspt.forms) {
        loadForm();
      } else {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (window.hbspt && window.hbspt.forms) {
            clearInterval(interval);
            loadForm();
          }
          if (attempts > 50) clearInterval(interval);
        }, 100);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [containerId, portalId, formId, region]);
};
